import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DEPARTMENTS, MUNICIPALITIES, departmentByCode, municipalitiesOf, municipalityByCode, nitDigits } from '@xiltrion/colombia';
import { ENTITY_PROFILE_LABEL } from '@xiltrion/identidad';
import { ENTITY_STATUS_LABEL } from '@suite/contracts';
import { NitConDv } from './NitConDv';
import {
  BRAND_LOGO_ACCEPT, BRAND_LOGO_MAX_BYTES, BRAND_THEMES, COMMON_CURRENCIES, COMMON_TIMEZONES, HEX_COLOR, brandPrimary, readableOn,
  type BrandThemeCode,
} from '@xiltrion/entidad';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { EstadoCargando, EstadoError } from '@xiltrion/ui/bloques/estados';
import { SelectorBuscable, SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { Attachment, AttachmentAction, AttachmentActions, AttachmentContent, AttachmentDescription, AttachmentMedia, AttachmentTitle, AttachmentTrigger } from '@xiltrion/ui/attachment';
import { Button } from '@xiltrion/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xiltrion/ui/card';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@xiltrion/ui/field';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { avisar } from '@xiltrion/ui/sonner';
import { Spinner } from '@xiltrion/ui/spinner';
import { useFileUpload } from '@xiltrion/ui/use-file-upload';
import { apiDelete, apiGet, apiPatch, apiPut, errorMessage } from '@/api/client';
import { fileToBase64 } from '@/api/files';
import type { DataLocationRow, EntityDetail, EntitySettingsView } from '@/api/types';
import { useSession } from '@/app/session';

const avisarError = (e: unknown) => avisar.error(errorMessage(e));

/** Municipios del departamento elegido o, sin él, todos con su departamento (para buscar por nombre). */
function municipalityOptions(departmentCode: string) {
  if (departmentCode) return municipalitiesOf(departmentCode).map((m) => ({ value: m.code, label: m.name }));
  return MUNICIPALITIES.map((m) => ({ value: m.code, label: `${m.name} · ${departmentByCode(m.departmentCode)?.name ?? ''}` }));
}

/** Quién cambió algo y cuándo, en una línea. */
export function Cambio({ info }: { info?: { updatedAt: string; updatedBy: string | null } }) {
  if (!info) return null;
  return <span className="tnum mt-1 block text-xs text-muted-foreground">Cambiado {new Date(info.updatedAt).toLocaleString('es-CO')}{info.updatedBy ? ` por ${info.updatedBy}` : ''}</span>;
}

export function useEntitySettings(slug: string) {
  return useQuery({ queryKey: ['entity-settings', slug], queryFn: () => apiGet<EntitySettingsView>(`/entities/${slug}/settings`) });
}

/**
 * Perfil de la entidad (GUIA-AGENTES.md §3.11): la ficha que todas las apps muestran o imprimen de la organización y,
 * para un superadministrador, el tipo, el estado y la ubicación de los datos.
 */
export function EntityProfileTab({ entity, superadmin }: { entity: EntityDetail; superadmin: boolean }) {
  const q = useEntitySettings(entity.slug);
  if (q.error) return <EstadoError error={q.error} />;
  if (!q.data) return <EstadoCargando />;
  if (!superadmin) return <div className="max-w-3xl"><DetailsCard entity={entity} view={q.data} /></div>;
  return (
    <div className="grid items-start gap-6 xl:grid-cols-[3fr_2fr] [&>*]:min-w-0">
      <DetailsCard entity={entity} view={q.data} />
      <PlatformCard entity={entity} />
    </div>
  );
}

/** Marca de la entidad (GUIA-AGENTES.md §3.11): el logo y el color de las pantallas de todas las apps. */
export function EntityBrandingTab({ entity }: { entity: EntityDetail }) {
  const q = useEntitySettings(entity.slug);
  if (q.error) return <EstadoError error={q.error} />;
  if (!q.data) return <EstadoCargando />;
  return <div className="max-w-2xl"><BrandingCard slug={entity.slug} view={q.data} /></div>;
}

function DetailsCard({ entity, view }: { entity: EntityDetail; view: EntitySettingsView }) {
  const qc = useQueryClient();
  const { refresh } = useSession();
  const d = view.details;
  const initial = () => ({
    name: entity.name, nit: entity.nit ?? '', address: d.address ?? '', departmentCode: d.departmentCode ?? '', municipalityCode: d.municipalityCode ?? '',
    city: d.city ?? '', postalCode: d.postalCode ?? '', phone: d.phone ?? '', email: d.email ?? '', website: d.website ?? '', timezone: d.timezone, currency: d.currency, locale: d.locale,
  });
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(initial()), [view, entity.name, entity.nit]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (patch: Partial<ReturnType<typeof initial>>) => setForm((f) => ({ ...f, ...patch }));
  const digits = nitDigits(form.nit);

  const save = async () => {
    setSaving(true);
    try {
      const { name, nit, ...details } = form;
      if (name !== entity.name || (nit || null) !== entity.nit) await apiPatch(`/entities/${entity.slug}`, { name, nit: digits || null });
      await apiPut(`/entities/${entity.slug}/settings/details`, { ...details, departmentCode: details.departmentCode || null, municipalityCode: details.municipalityCode || null });
      avisar.success('Ficha guardada. Las apps la reciben en unos segundos.');
      await Promise.all([qc.invalidateQueries({ queryKey: ['entity-settings', entity.slug] }), qc.invalidateQueries({ queryKey: ['entity', entity.slug] }), refresh()]);
    } catch (e) {
      avisarError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Ficha de la entidad</CardTitle>
        <CardDescription>Los datos que todas las apps usan en pantallas, correos y documentos. <Cambio info={view.changed.details} /></CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); void save(); }}>
          <FieldGroup>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field><FieldLabel htmlFor="p-name">Nombre</FieldLabel><Input id="p-name" required value={form.name} onChange={(e) => set({ name: e.target.value })} /></Field>
              <NitConDv id="p-nit" value={form.nit} onChange={(nit) => set({ nit })} />
            </div>
            <Field><FieldLabel htmlFor="p-address">Dirección</FieldLabel><Input id="p-address" value={form.address} onChange={(e) => set({ address: e.target.value })} /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field><FieldLabel>Departamento</FieldLabel><SelectorBuscable value={form.departmentCode || null} limpiable options={DEPARTMENTS.map((x) => ({ value: x.code, label: x.name }))} onChange={(v) => set({ departmentCode: v ?? '', ...(v && form.municipalityCode.startsWith(v) ? {} : { municipalityCode: '', city: '' }) })} /></Field>
              <Field>
                <FieldLabel>Municipio</FieldLabel>
                <SelectorBuscable value={form.municipalityCode || null} limpiable placeholder={form.departmentCode ? 'Elige el municipio' : 'Busca el municipio'}
                  options={municipalityOptions(form.departmentCode)}
                  onChange={(v) => { const m = v ? municipalityByCode(v) : undefined; set({ municipalityCode: m?.code ?? '', city: m?.name ?? '', ...(m ? { departmentCode: m.departmentCode } : {}) }); }} />
              </Field>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field><FieldLabel htmlFor="p-phone">Teléfono</FieldLabel><Input id="p-phone" className="tnum" value={form.phone} onChange={(e) => set({ phone: e.target.value })} /></Field>
              <Field><FieldLabel htmlFor="p-email">Correo de contacto</FieldLabel><Input id="p-email" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} /></Field>
              <Field><FieldLabel htmlFor="p-postal">Código postal</FieldLabel><Input id="p-postal" className="tnum" value={form.postalCode} onChange={(e) => set({ postalCode: e.target.value })} /></Field>
            </div>
            <Field><FieldLabel htmlFor="p-web">Sitio web</FieldLabel><Input id="p-web" type="url" value={form.website} onChange={(e) => set({ website: e.target.value })} placeholder="https://www.entidad.gov.co" /></Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field><FieldLabel>Zona horaria</FieldLabel><SelectorBuscable value={form.timezone} editable options={COMMON_TIMEZONES.map((z) => ({ value: z, label: z }))} onChange={(v) => set({ timezone: v ?? 'America/Bogota' })} /><FieldDescription>Fechas, plazos y el calendario hábil de todas las apps.</FieldDescription></Field>
              <Field><FieldLabel>Moneda</FieldLabel><SelectorSimple value={form.currency} options={COMMON_CURRENCIES.map((c) => ({ value: c.code, label: `${c.code} · ${c.label}` }))} onChange={(v) => set({ currency: v ?? 'COP' })} /></Field>
            </div>
          </FieldGroup>
          <Button type="submit" disabled={saving}>{saving && <Spinner />}Guardar ficha</Button>
        </form>
      </CardContent>
    </Card>
  );
}

/** Lo que solo cambia Datalog: tipo, estado y ubicación de los datos. */
function PlatformCard({ entity }: { entity: EntityDetail }) {
  const qc = useQueryClient();
  const { refresh } = useSession();
  const [form, setForm] = useState({ status: entity.status, profile: entity.profile, dataLocation: entity.dataLocation });
  const { data: locations = [] } = useQuery({ queryKey: ['data-locations'], queryFn: () => apiGet<DataLocationRow[]>('/admin/data-locations') });
  const save = () => {
    const body = { status: form.status, profile: form.profile, dataLocation: form.dataLocation };
    void apiPatch(`/entities/${entity.slug}`, body).then(() => { avisar.success('Guardado'); return Promise.all([qc.invalidateQueries({ queryKey: ['entity', entity.slug] }), refresh()]); }, avisarError);
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Administración de Datalog</CardTitle>
        <CardDescription>Solo un superadministrador los ve y los cambia.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-6" onSubmit={(e) => { e.preventDefault(); save(); }}>
          <FieldGroup>
            <Field><FieldLabel>Tipo de entidad</FieldLabel><SelectorSimple value={form.profile} options={Object.entries(ENTITY_PROFILE_LABEL).map(([value, label]) => ({ value, label }))} onChange={(v) => setForm({ ...form, profile: v ?? form.profile })} /></Field>
            <Field><FieldLabel>Estado</FieldLabel><SelectorSimple value={form.status} options={Object.entries(ENTITY_STATUS_LABEL).map(([value, label]) => ({ value, label }))} onChange={(v) => setForm({ ...form, status: v ?? form.status })} /><FieldDescription>Suspender o archivar también se comunica a las apps aprovisionadas.</FieldDescription></Field>
            <Field><FieldLabel>Ubicación de los datos</FieldLabel><SelectorSimple value={form.dataLocation} options={locations.map((l) => ({ value: l.code, label: l.name }))} onChange={(v) => setForm({ ...form, dataLocation: v ?? form.dataLocation })} /><FieldDescription>Aplica a las apps que se aprovisionen desde ahora. Cambiarla no mueve las bases que ya existen: en cada app, <code>pnpm tenant:move {entity.slug} {form.dataLocation}</code>.</FieldDescription></Field>
          </FieldGroup>
          <Button type="submit">Guardar</Button>
        </form>
      </CardContent>
    </Card>
  );
}

function BrandingCard({ slug, view }: { slug: string; view: EntitySettingsView }) {
  const qc = useQueryClient();
  const { refresh } = useSession();
  const [theme, setTheme] = useState<BrandThemeCode>(view.branding.theme);
  const [color, setColor] = useState(view.branding.customColor ?? '#1d4ed8');
  const [uploading, setUploading] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  useEffect(() => { setTheme(view.branding.theme); setColor(view.branding.customColor ?? '#1d4ed8'); }, [view.branding.theme, view.branding.customColor]);
  const primary = brandPrimary(theme, theme === 'CUSTOM' && HEX_COLOR.test(color) ? color : null);
  const done = async (message: string) => {
    avisar.success(message);
    await Promise.all([qc.invalidateQueries({ queryKey: ['entity-settings', slug] }), qc.invalidateQueries({ queryKey: ['entity', slug] }), refresh()]);
  };
  const run = async (fn: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await fn();
      await done(message);
    } catch (e) {
      avisarError(e);
    } finally {
      setBusy(false);
    }
  };
  const [, { openFileDialog, getInputProps, clearFiles }] = useFileUpload({
    accept: BRAND_LOGO_ACCEPT,
    maxSize: BRAND_LOGO_MAX_BYTES,
    onError: (errs) => avisar.error(errs.join(' · ')),
    onFilesAdded: (added) => {
      const file = added[0]?.file;
      if (!(file instanceof File)) return;
      setUploading(file.name);
      void run(async () => apiPut(`/entities/${slug}/settings/logo`, { fileName: file.name, base64: await fileToBase64(file) }), 'Logo actualizado').finally(() => { clearFiles(); setUploading(null); });
    },
  });
  const logo = view.branding.logo;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Marca</CardTitle>
        <CardDescription>Logo y color de las pantallas de todas las apps: ingreso, portal y barra lateral. <Cambio info={view.changed.branding} /></CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center gap-4 rounded-lg border border-border bg-muted p-4">
          {view.branding.logo ? <img src={view.branding.logo.url} alt="Logo de la entidad" className="h-14 max-w-48 object-contain" /> : <div className="flex h-14 w-14 items-center justify-center rounded-md bg-card text-muted-foreground"><Icono nombre="mdi:image-outline" /></div>}
          <div className="flex-1 space-y-2">
            <div className="text-sm font-medium">{view.details.name}</div>
            {primary ? <span className="inline-flex rounded-md px-3 py-1 text-xs font-medium" style={{ background: primary, color: readableOn(primary) }}>Botón principal</span> : <Button size="sm" type="button">Botón principal</Button>}
          </div>
        </div>
        <Field>
          <FieldLabel>Logo</FieldLabel>
          {uploading ? (
            <Attachment state="uploading" className="w-full">
              <AttachmentMedia><Spinner /></AttachmentMedia>
              <AttachmentContent><AttachmentTitle>{uploading}</AttachmentTitle><AttachmentDescription>Subiendo el logo…</AttachmentDescription></AttachmentContent>
            </Attachment>
          ) : logo ? (
            <Attachment className="w-full">
              <AttachmentMedia><img src={logo.url} alt="" className="size-full object-contain p-0.5" /></AttachmentMedia>
              <AttachmentContent><AttachmentTitle>{logo.fileName}</AttachmentTitle><AttachmentDescription className="tnum">{Math.ceil(logo.sizeBytes / 1024)} KB</AttachmentDescription></AttachmentContent>
              <AttachmentActions><AttachmentAction type="button" aria-label="Quitar el logo" disabled={busy} onClick={() => setConfirmRemove(true)}><Icono nombre="mdi:close" /></AttachmentAction></AttachmentActions>
            </Attachment>
          ) : (
            <Attachment state="idle" className="w-full">
              <AttachmentMedia><Icono nombre="mdi:upload" /></AttachmentMedia>
              <AttachmentContent><AttachmentTitle>Elegir el logo</AttachmentTitle><AttachmentDescription>PNG, JPEG o WebP de hasta <span className="tnum">{BRAND_LOGO_MAX_BYTES / 1024} KB</span>, con fondo transparente si es posible</AttachmentDescription></AttachmentContent>
              <AttachmentTrigger aria-haspopup="dialog" aria-label="Elegir el logo" disabled={busy} onClick={openFileDialog} />
            </Attachment>
          )}
          <input {...getInputProps()} className="sr-only" tabIndex={-1} aria-label="Elegir el logo" />
        </Field>
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); void run(() => apiPut(`/entities/${slug}/settings/branding`, { theme, customColor: theme === 'CUSTOM' ? color : null }), 'Color guardado'); }}>
          <Field><FieldLabel>Color principal</FieldLabel><SelectorSimple value={theme} options={[...BRAND_THEMES.map((t) => ({ value: t.code, label: t.label })), { value: 'CUSTOM', label: 'Personalizado' }]} onChange={(v) => setTheme((v as BrandThemeCode) ?? 'DEFAULT')} /></Field>
          {theme === 'CUSTOM' && <Field><FieldLabel htmlFor="b-color">Color personalizado</FieldLabel><div className="flex gap-2"><Input id="b-color" type="color" className="h-9 w-14 p-1" value={HEX_COLOR.test(color) ? color : '#000000'} onChange={(e) => setColor(e.target.value)} /><Input className="tnum" value={color} onChange={(e) => setColor(e.target.value)} placeholder="#1d4ed8" /></div></Field>}
          <Button type="submit" disabled={busy || (theme === 'CUSTOM' && !HEX_COLOR.test(color))}>{busy && <Spinner />}Guardar color</Button>
        </form>
      </CardContent>
      <ConfirmarDialogo abierto={confirmRemove} onAbiertoChange={setConfirmRemove} titulo="¿Quitar el logo?" descripcion="Las apps vuelven a mostrar solo el nombre de la entidad." textoConfirmar="Quitar" destructivo onConfirmar={() => run(() => apiDelete(`/entities/${slug}/settings/logo`), 'Logo quitado')} />
    </Card>
  );
}
